import { Icon } from "@iconify/react/dist/iconify.js";
import { DateInput } from "@mantine/dates";
import { useDebouncedState } from "@mantine/hooks";
import React, { useEffect } from "react";

import { getDaysInMonth } from "~/lib/date-helpers";

import { InputDateParams } from "./input-types";

function InputDate<TData>(params: InputDateParams<TData>) {
  const { label, name, form, error, onChange, ...opts } = params;

  const [tempValue, setTempValue] = useDebouncedState<string[]>([], 150);

  useEffect(() => {
    try {
      const [day, month, year] = tempValue;
      const now = new Date();

      // Return early if all values are empty
      if (!day && !month && !year) return;

      let setYear: string;
      let setMonth: string;
      let setDay: string;

      // determine year
      if (!year || isNaN(+year)) setYear = now.getFullYear().toString();
      else if (
        [0, 1].includes(+year) ||
        [...Array(9).keys()].map((e) => e + 10).includes(+year.slice(0, 2))
      )
        setYear = "1900";
      else setYear = year.padEnd(now.getFullYear().toString().length, "0");

      // determine month
      if (!month || isNaN(+month))
        setMonth = (now.getMonth() + 1).toString().padStart(2, "0");
      else if (+month == 0) setMonth = "01";
      else if (+month > 12) setMonth = "12";
      else setMonth = month.padStart(2, "0");

      // determine day
      if (!day || isNaN(+day))
        setDay = now.getDay().toString().toString().padStart(2, "0");
      else if (+day == 0) setDay = "01";
      else if (+day > getDaysInMonth(+setMonth, +setYear))
        setDay = getDaysInMonth(+setMonth, +setYear).toString();
      else setDay = day.padStart(2, "0");

      // set the form field value to the constructed date
      form.setFieldValue(
        name,
        new Date(`${setYear}-${setMonth}-${setDay}`) as any,
      );
    } catch (e) {
      console.error(e);
    }
  }, [tempValue]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const allowedKeys = ["Backspace", "Tab", "ArrowLeft", "ArrowRight"];
    const position = (e.target as HTMLInputElement).selectionStart;

    if (allowedKeys.includes(e.key)) {
      return;
    }

    if (position === 10) return e.preventDefault();

    if (position === 2 || position === 5) {
      if (e.key !== "-") {
        e.preventDefault();
      }
    } else {
      if (e.key < "0" || e.key > "9") {
        e.preventDefault();
      }
    }
  };

  const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const allowedKeys = ["Backspace", "Tab", "ArrowLeft", "ArrowRight"];
    const position = (e.target as HTMLInputElement).selectionStart;

    if (allowedKeys.includes(e.key)) {
      return;
    }

    if (position === 10) return e.preventDefault();

    if (position === 2 || position === 5) {
      if (e.key !== "-") {
        e.preventDefault();
      }
    } else {
      if (e.key < "0" || e.key > "9") {
        e.preventDefault();
      }
    }
  };

  return (
    <DateInput
      label={label}
      size="sm"
      styles={{ label: { fontWeight: 600 } }}
      minDate={new Date("1900-01-01")}
      valueFormat="DD-MM-YYYY"
      onInputCapture={(e) => {
        const val = (e.target as HTMLInputElement).value;
        if (!/^\d{2}-\d{2}-\d{4}$/.test(val)) {
          e.preventDefault();
        }

        const value: string[] = (
          ((e?.target ?? {}) as any)?.value?.toString() ?? ""
        ).split("-");
        setTempValue(value);
      }}
      autoComplete="off"
      rightSection={<Icon icon="tabler:calendar" className="w-4 h-4" />}
      {...opts}
      aria-label={
        typeof label == "string" && !params["aria-label"]
          ? label
          : params["aria-label"]
      }
      {...form.getInputProps(name)}
      onChange={(e) => {
        form.getInputProps(name).onChange(e);
        !!onChange && onChange(e);
      }}
      error={error ?? form.getInputProps(name).error}
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyUp}
      locale="id_ID"
    />
  );
}

export default InputDate;
